import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { IDropdownOption } from '@app/shared/common/dropdown/DropdownOption';
import AppLocalizationService from '@app/shared/common/localization/app-localization.service';
import { ISourceCategoryDropdownOption, ISourceSelection, nullable } from '@shared/common/common-types';
import {
    ClientMatchSourceCategoryResponseModel,
    ClientsServiceProxy,
    EmployeesPaginatedRequestModel,
    EmployeesServiceProxy,
    MarketingCampaignPaginatedRequestModel,
    MarketingCampaignsServiceProxy,
    NomenclaturesPaginatedRequestModel,
    SocialMediaServiceProxy,
    SourceCategoriesPaginatedRequestModel,
    SourceCategoriesServiceProxy,
    SourceDetailLookupResponseModel,
    SourceDetailType,
    WebsitesServiceProxy,
} from '@shared/service-proxies/service-proxies';
import {
    clientDropdownMapper,
    defaultDropdownMapper,
    employeeDropdownMapper,
    mapSourceDetailDropdownOption,
} from '@shared/utils/dropdown.utils';
import { first, isNil, omitBy } from 'lodash';
import { map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
class SourceCategoriesService {
    private static readonly PageSize = 50;

    constructor(
        private readonly proxy: SourceCategoriesServiceProxy,
        private readonly clientsService: ClientsServiceProxy,
        private readonly employeesService: EmployeesServiceProxy,
        private readonly websitesService: WebsitesServiceProxy,
        private readonly socialMediaService: SocialMediaServiceProxy,
        private readonly marketingService: MarketingCampaignsServiceProxy,
        private readonly localizationService: AppLocalizationService,
    ) {
    }

    getRootSources(name?: nullable<string>, onlyActive = true): Observable<IDropdownOption[]> {
        return this.proxy
            .getAll({
                name,
                onlyActive,
                withParent: false,
                pageSize: SourceCategoriesService.PageSize,
            } as SourceCategoriesPaginatedRequestModel)
            .pipe(map(({ items }) => items?.map(defaultDropdownMapper) ?? []));
    }

    getSubSources(
        parentId?: nullable<number>,
        name?: nullable<string>,
        onlyActive = true,
    ): Observable<ISourceCategoryDropdownOption[]> {
        if (isNil(parentId)) {
            return of([]);
        }

        return this.proxy
            .getAll({
                name,
                parentId,
                onlyActive,
                pageSize: SourceCategoriesService.PageSize,
            } as SourceCategoriesPaginatedRequestModel)
            .pipe(map(({ items }) => items?.map((i) => ({
                key: i.id.toString(),
                value: i.name,
                detailType: i.detailType,
            })) as ISourceCategoryDropdownOption[] ?? []));
    }

    getSourceDetailPlaceholder(detailType?: nullable<SourceDetailType>): string {
        switch (detailType) {
            case SourceDetailType.Client:
                return this.localizationService.l('SearchContactByPhoneOrEmail');
            case SourceDetailType.Employee:
                return this.localizationService.l('SearchEmployeeByEmailPlaceholder');
            case SourceDetailType.Website:
                return this.localizationService.l('SearchWebsiteByNamePlaceholder');
            case SourceDetailType.SocialMedia:
                return this.localizationService.l('SearchSocialMediaByNamePlaceholder');
            case SourceDetailType.MarketingCampaign:
                return this.localizationService.l('SearchMarketingCampaignByNamePlaceholder');
            default:
                return '';
        }
    }

    getSourceDetails(detailType?: nullable<SourceDetailType>, searchString?: nullable<string>): Observable<IDropdownOption[]> {
        switch (detailType) {
            case SourceDetailType.Client:
                return this.clientsService
                    .getClientsPersonalData(searchString)
                    .pipe(map((items) => items.map(i => clientDropdownMapper({ id: i.id, name: i.fullName }))));
            case SourceDetailType.Employee:
                return this.employeesService
                    .getAll({ searchString, onlyActive: true } as EmployeesPaginatedRequestModel)
                    .pipe(map(({ items }) => items?.map(i => employeeDropdownMapper({
                        id: i.id,
                        fullName: [i.firstName, i.lastName].join(' '),
                        emailAddress: i.emailAddress,
                    })) ?? []));
            case SourceDetailType.Website:
                return this.websitesService
                    .getAll({ name: searchString, onlyActive: true } as NomenclaturesPaginatedRequestModel)
                    .pipe(map(({ items }) => items?.map(defaultDropdownMapper) ?? []));
            case SourceDetailType.SocialMedia:
                return this.socialMediaService
                    .getAll({ name: searchString, onlyActive: true } as NomenclaturesPaginatedRequestModel)
                    .pipe(map(({ items }) => items?.map(defaultDropdownMapper) ?? []));
            case SourceDetailType.MarketingCampaign:
                return this.marketingService
                    .getAll({ searchTerm: searchString } as MarketingCampaignPaginatedRequestModel)
                    .pipe(map(({ items }) => items?.map(defaultDropdownMapper) ?? []));
            default:
                return of([]);
        }
    }

    toSourceSelection(lookup: SourceDetailLookupResponseModel): ISourceSelection {
        return {
            source: this.toOption(lookup.sourceCategoryId, lookup.sourceCategoryName),
            subSource: this.toSubSourceOption(lookup.subSourceCategoryId, lookup.subSourceCategoryName, lookup.subSourceDetailType),
            sourceDetail: this.toSourceDetailOption(lookup),
        };
    }

    toClientSourceSelection(clientSource: ClientMatchSourceCategoryResponseModel): ISourceSelection {
        return {
            source: this.toOption(clientSource.sourceCategoryParentId, clientSource.sourceCategoryParentName),
            subSource: this.toSubSourceOption(
                clientSource.sourceCategoryId,
                clientSource.sourceCategoryName,
                clientSource.sourceCategoryDetailType,
            ),
            sourceDetail: mapSourceDetailDropdownOption(first(clientSource.sourceDetails)),
        };
    }

    applySourceSelection(form: FormGroup, selection: ISourceSelection): void {
        form.patchValue(omitBy(selection, isNil), { emitEvent: false });
    }

    private toSourceDetailOption(lookup: SourceDetailLookupResponseModel): nullable<IDropdownOption> {
        switch (lookup.subSourceDetailType) {
            case SourceDetailType.Website:
                return this.toOption(lookup.detailWebsiteId, lookup.detailWebsiteName);
            case SourceDetailType.SocialMedia:
                return this.toOption(lookup.detailSocialMediaId, lookup.detailSocialMediaName);
            case SourceDetailType.MarketingCampaign:
                return this.toOption(lookup.detailMarketingCampaignId, lookup.detailMarketingCampaignName);
            case SourceDetailType.Employee:
                return this.toOption(lookup.detailEmployeeId, lookup.detailEmployeeName);
            case SourceDetailType.Client:
                return this.toOption(lookup.detailClientId, lookup.detailClientName);
            default:
                return null;
        }
    }

    private toOption(id: nullable<number>, name: nullable<string>): nullable<IDropdownOption> {
        return isNil(id)
            ? null
            : { key: id.toString(), value: name ?? '' };
    }

    private toSubSourceOption(
        id: nullable<number>,
        name: nullable<string>,
        detailType: nullable<SourceDetailType>,
    ): nullable<ISourceCategoryDropdownOption> {
        return isNil(id)
            ? null
            : { key: id.toString(), value: name ?? '', detailType };
    }
}

export default SourceCategoriesService;
